import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { MutationKeys, QueryKeys, ScopedMutationKeys } from '~/services/loyalty/constants';
import { useBasket } from '~/features/basket';
import { useTranslation } from '~/shared/utils/translation';
import { useFrame } from '~/shared/utils';
import { useEvents } from '../../useEvents';

export const useReleaseVoucher = () => {
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { removePromoCode } = useBasket();
    const queryClient = useQueryClient();
    const { data: frame } = useFrame();
    const { releaseVoucherEvent } = useEvents(frame);
    const { translate } = useTranslation();

    type PromoId = string;

    return useMutation<void, Error, PromoId>({
        mutationKey: [MutationKeys.RELEASE_VOUCHER],
        mutationFn: async (promoId: PromoId) => {
            if (!promoId) trackError(translate('basket.couldNotFindCoupon'));
            await removePromoCode(promoId);
        },
        // scope is to ensure that the mutation runs in serial instead of parallel to
        // ensure full sync between FE and SFCC when removing a voucher
        scope: {
            id: ScopedMutationKeys.SCOPED_VOUCHERS,
        },
        onError: () => {
            const error = new Error(translate('basket.couldNotSaveChangesError'));
            trackError(error);
            throw error;
        },
        onSuccess: (_, promoId) => {
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.ACTIVE_VOUCHERS],
            });

            releaseVoucherEvent(promoId);
        },
    });
};
